import React, { forwardRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { get } from 'lodash';

import { Typography } from '@material-ui/core';
import { DialogActions, DialogAdaptive, DialogContent, DialogTitle } from '../../components/material-ui/Dialog';
import { FormControl } from '../../components/material-ui/FormControl';
import { TextField } from '../../components/material-ui/TextField';
import { DialogControls } from '../../components/DialogControls/DialogControls';
import { DateTimePicker } from '../../components/DateTimePicker/DateTimePicker';
import { getTranslateText } from '../../service/utils';
import { useAirportServiceModal } from './useAirportServiceModal';
import { useStyles } from './useStyles';

const InputLabelProps = { shrink: true };

export const AirportServiceModal = forwardRef(({ legId, service }, ref) => {
    const { t: translate } = useTranslation();
    const classes = useStyles();

    const { actions, inputs, status, handleInputChange, handleDateChange, handleSubmit } = useAirportServiceModal(legId, service);

    const dateBegin = get(inputs, 'dateBegin');
    const dateEnd = get(inputs, 'dateEnd');
    const dateBeginReal = get(inputs, 'dateBeginReal');
    const dateEndReal = get(inputs, 'dateEndReal');

    const InputProps = useMemo(
        () => ({
            classes: {
                input: classes.field,
            },
        }),
        []
    );

    return (
        <DialogAdaptive open={true} maxWidth="md" fullScreenBreakpoint="sm">
            <DialogTitle className={classes.title}>{translate('modal.editService')}</DialogTitle>
            <form ref={ref} onSubmit={handleSubmit}>
                <DialogContent className={classes.content}>
                    <FormControl className={classes.formControl}>
                        <Typography variant="body1" className={classes.label}>
                            {translate('airportServices.serviceType')}
                        </Typography>
                        <Typography variant="body1" className={classes.text}>
                            {getTranslateText('name', service)}
                        </Typography>
                    </FormControl>
                    <DateTimePicker
                        className={classes.picker}
                        labelTextDate={translate('airportServices.beginPlan')}
                        name="dateBegin"
                        value={dateBegin}
                        error={Boolean(status.dateBegin)}
                        onChange={handleDateChange}
                        fullWidth={true}
                        InputProps={InputProps}
                        InputLabelProps={InputLabelProps}
                    />
                    <DateTimePicker
                        className={classes.picker}
                        labelTextDate={translate('airportServices.endPlan')}
                        name="dateEnd"
                        value={dateEnd}
                        error={Boolean(status.dateEnd)}
                        onChange={handleDateChange}
                        fullWidth={true}
                        InputProps={InputProps}
                        InputLabelProps={InputLabelProps}
                    />
                    <DateTimePicker
                        className={classes.picker}
                        labelTextDate={translate('airportServices.beginReal')}
                        name="dateBeginReal"
                        value={dateBeginReal}
                        error={Boolean(status.dateBeginReal)}
                        onChange={handleDateChange}
                        fullWidth={true}
                        InputProps={InputProps}
                        InputLabelProps={InputLabelProps}
                    />
                    <DateTimePicker
                        className={classes.picker}
                        labelTextDate={translate('airportServices.endReal')}
                        name="dateEndReal"
                        value={dateEndReal}
                        error={Boolean(status.dateEndReal)}
                        onChange={handleDateChange}
                        fullWidth={true}
                        InputProps={InputProps}
                        InputLabelProps={InputLabelProps}
                    />
                    <FormControl className={classnames(classes.formControl, classes.quantity)}>
                        <TextField
                            id="quantity"
                            name="quantity"
                            label={translate('airportServices.quantity')}
                            value={get(inputs, 'quantity')}
                            onChange={handleInputChange}
                            type="number"
                            InputLabelProps={InputLabelProps}
                            InputProps={InputProps}
                            placeholder={translate('airportServices.quantity')}
                            className={classes.field}
                        />
                    </FormControl>
                    <FormControl className={classnames(classes.formControl, classes.priority)}>
                        <TextField
                            name="priority"
                            label={translate('airportServices.priority')}
                            placeholder={translate('airportServices.priority')}
                            onChange={handleInputChange}
                            value={get(inputs, 'priority', '')}
                            className={classes.field}
                            InputLabelProps={InputLabelProps}
                            InputProps={InputProps}
                        />
                    </FormControl>
                    <FormControl className={classes.formControl}>
                        <TextField
                            name="comment"
                            label={translate('comment')}
                            placeholder={translate('comment')}
                            onChange={handleInputChange}
                            fullWidth={true}
                            multiline={true}
                            value={get(inputs, 'comment', '')}
                            className={classes.textarea}
                            InputLabelProps={InputLabelProps}
                            InputProps={InputProps}
                        />
                    </FormControl>
                </DialogContent>
                <DialogActions className={classes.actions}>
                    <DialogControls actions={actions} />
                </DialogActions>
            </form>
        </DialogAdaptive>
    );
});
